import type { AudioClip, AutomationCurve, Project } from '@/domain/types';
import {
  MAX_CLIP_GAIN,
  clipGainAt,
  clipSourceAtOffset,
  MIN_CLIP_GAIN,
  makeGainPoint,
  sortedGainPoints,
  thinGainPoints,
  withGainPoint,
  withoutGainPoints,
} from '@/domain/clipGain';
import { newGainPointId } from '@/util/id';
import { defineCommand } from './registry';
import { enumParam, idParam, idsParam, intParam, numberParam } from './params';
import { clipStartSeconds, tempoMapOf } from '@/domain/selectors';
import { ticksToSeconds } from '@/timing/tempoMap';

/**
 * Editing a clip's own gain envelope.
 *
 * Every command here is a rewrite of one clip's point list, which is the whole
 * feature: the envelope is data on the clip, so it saves, undoes, copies and
 * travels in a `.dawproject` with no handling of its own — and a split or a trim
 * needs no arithmetic at all, because the points are in source seconds. See
 * `domain/clipGain.ts`.
 */

const CURVES: readonly AutomationCurve[] = ['linear', 'hold', 'smooth'];
/** More points than a person draws on one clip, by a wide margin. */
const MAX_POINTS = 2000;

/** The clip, if it is one that can carry an envelope. */
function audioClip(project: Project, clipId: string): AudioClip | null {
  const clip = project.clips[clipId];
  return clip && clip.kind === 'audio' ? clip : null;
}

function withEnvelope(project: Project, clipId: string, points: AudioClip['gainEnvelope']): Project {
  const clip = audioClip(project, clipId);
  if (!clip) return project;
  return { ...project, clips: { ...project.clips, [clipId]: { ...clip, gainEnvelope: points } } };
}

export const addClipGainPoint = defineCommand<{
  clipId: string;
  pointId: string;
  sourceSec: number;
  gain: number;
  curve: AutomationCurve;
}>({
  type: 'clipGain.addPoint',
  label: () => 'Add a clip gain point',
  parse: (raw) => {
    const clipId = idParam(raw.clipId);
    const pointId = idParam(raw.pointId);
    const sourceSec = numberParam(raw.sourceSec, 0, 24 * 3600);
    const gain = numberParam(raw.gain, MIN_CLIP_GAIN, MAX_CLIP_GAIN, 1);
    const curve = enumParam(raw.curve, CURVES, 'linear');
    if (!clipId || !pointId || sourceSec === null || gain === null || !curve) return null;
    return { clipId, pointId, sourceSec, gain, curve };
  },
  apply: (project, p) => {
    const clip = audioClip(project, p.clipId);
    if (!clip || clip.gainEnvelope.length >= MAX_POINTS) return project;
    return withEnvelope(project, p.clipId, withGainPoint(clip.gainEnvelope, makeGainPoint(p.pointId, p.sourceSec, p.gain, p.curve)));
  },
});

/**
 * Drags a point.
 *
 * Position and level move together because that is one gesture, and it
 * coalesces on the point so a drag is one undo rather than one per pixel.
 */
export const moveClipGainPoint = defineCommand<{
  clipId: string;
  pointId: string;
  sourceSec: number;
  gain: number;
}>({
  type: 'clipGain.movePoint',
  label: () => 'Move a clip gain point',
  parse: (raw) => {
    const clipId = idParam(raw.clipId);
    const pointId = idParam(raw.pointId);
    const sourceSec = numberParam(raw.sourceSec, 0, 24 * 3600);
    const gain = numberParam(raw.gain, MIN_CLIP_GAIN, MAX_CLIP_GAIN);
    if (!clipId || !pointId || sourceSec === null || gain === null) return null;
    return { clipId, pointId, sourceSec, gain };
  },
  coalesceKey: (p) => `clipGain:${p.clipId}:${p.pointId}`,
  apply: (project, p) => {
    const clip = audioClip(project, p.clipId);
    const existing = clip?.gainEnvelope.find((point) => point.id === p.pointId);
    if (!clip || !existing) return project;
    const moved = makeGainPoint(p.pointId, p.sourceSec, p.gain, existing.curve);
    if (moved.sourceSec === existing.sourceSec && moved.gain === existing.gain) return project;
    return withEnvelope(project, p.clipId, withGainPoint(clip.gainEnvelope, moved));
  },
});

export const removeClipGainPoints = defineCommand<{ clipId: string; pointIds: string[] }>({
  type: 'clipGain.removePoints',
  label: (p) => (p.pointIds.length === 1 ? 'Delete a clip gain point' : 'Delete clip gain points'),
  parse: (raw) => {
    const clipId = idParam(raw.clipId);
    const pointIds = idsParam(raw.pointIds, MAX_POINTS);
    if (!clipId || !pointIds || pointIds.length === 0) return null;
    return { clipId, pointIds };
  },
  apply: (project, p) => {
    const clip = audioClip(project, p.clipId);
    if (!clip) return project;
    const kept = withoutGainPoints(clip.gainEnvelope, p.pointIds);
    if (kept.length === clip.gainEnvelope.length) return project;
    return withEnvelope(project, p.clipId, kept);
  },
});

export const setClipGainCurve = defineCommand<{ clipId: string; pointId: string; curve: AutomationCurve }>({
  type: 'clipGain.setCurve',
  label: () => 'Change a clip gain segment',
  parse: (raw) => {
    const clipId = idParam(raw.clipId);
    const pointId = idParam(raw.pointId);
    const curve = enumParam(raw.curve, CURVES);
    if (!clipId || !pointId || !curve) return null;
    return { clipId, pointId, curve };
  },
  apply: (project, p) => {
    const clip = audioClip(project, p.clipId);
    if (!clip || !clip.gainEnvelope.some((point) => point.id === p.pointId)) return project;
    return withEnvelope(
      project,
      p.clipId,
      clip.gainEnvelope.map((point) => (point.id === p.pointId ? { ...point, curve: p.curve } : point)),
    );
  },
});

/**
 * Removes the points of a ride that the line through their neighbours already
 * covers. See `thinGainPoints` for what is and is not touched.
 */
export const thinClipGain = defineCommand<{ clipIds: string[]; tolerance: number }>({
  type: 'clipGain.thin',
  label: () => 'Thin the clip gain ride',
  parse: (raw) => {
    const clipIds = idsParam(raw.clipIds);
    const tolerance = numberParam(raw.tolerance, 0, 1);
    return clipIds && clipIds.length > 0 && tolerance !== null ? { clipIds, tolerance } : null;
  },
  apply: (project, p) => {
    let next = project;
    for (const clipId of p.clipIds) {
      const clip = audioClip(next, clipId);
      if (!clip || clip.gainEnvelope.length <= 2) continue;
      const thinned = thinGainPoints(clip.gainEnvelope, p.tolerance);
      if (thinned.length === clip.gainEnvelope.length) continue;
      next = withEnvelope(next, clipId, thinned);
    }
    return next;
  },
});

export const clearClipGain = defineCommand<{ clipIds: string[] }>({
  type: 'clipGain.clear',
  label: () => 'Clear the clip gain envelope',
  parse: (raw) => {
    const clipIds = idsParam(raw.clipIds);
    return clipIds && clipIds.length > 0 ? { clipIds } : null;
  },
  apply: (project, p) => {
    let next = project;
    for (const clipId of p.clipIds) {
      const clip = audioClip(next, clipId);
      if (!clip || clip.gainEnvelope.length === 0) continue;
      next = withEnvelope(next, clipId, []);
    }
    return next;
  },
});

/**
 * Flattens an envelope into the clip's static gain, at a chosen moment.
 *
 * The way out of a ride that turned out to be a level: take the level the
 * envelope holds somewhere, fold it into `gain`, and drop the points. Offered
 * because "undo all of this but keep where it ended up" is otherwise a fiddly
 * manual job.
 */
export const flattenClipGain = defineCommand<{ clipId: string; atSourceSec: number }>({
  type: 'clipGain.flatten',
  label: () => 'Flatten the clip gain envelope',
  parse: (raw) => {
    const clipId = idParam(raw.clipId);
    const atSourceSec = numberParam(raw.atSourceSec, 0, 24 * 3600, 0);
    if (!clipId || atSourceSec === null) return null;
    return { clipId, atSourceSec };
  },
  apply: (project, p) => {
    const clip = audioClip(project, p.clipId);
    if (!clip || clip.gainEnvelope.length === 0) return project;
    const level = sortedGainPoints(clip.gainEnvelope);
    const gain = Math.min(
      MAX_CLIP_GAIN,
      Math.max(MIN_CLIP_GAIN, clip.gain * clipGainAt(level, p.atSourceSec)),
    );
    return {
      ...project,
      clips: { ...project.clips, [p.clipId]: { ...clip, gain, gainEnvelope: [] } },
    };
  },
});

/** Seconds from a clip's start to a moment of the timeline, which may be outside it. */
function clipOffsetAtTick(project: Project, clip: AudioClip, tick: number): number {
  return ticksToSeconds(tempoMapOf(project), tick) - clipStartSeconds(project, clip);
}

/**
 * Puts a copied ride onto other clips.
 *
 * The points arrive as offsets from a clip's start, not as source positions,
 * which is the only version of this that is useful. Four takes of the same
 * phrase are four different files; what they have in common is *where in the
 * clip* the phrase sits. So a paste lands the ride at the same place in the
 * target, whatever audio the target happens to be reading — and a clip playing
 * at a different rate gets the ride stretched to match, because the offsets are
 * converted through that clip's own mapping.
 *
 * Whatever was on the target is replaced. Merging two rides has no obvious
 * meaning, and a paste that silently combined with what was already there would
 * be impossible to undo by eye.
 *
 * With `atTick` the paste is *aimed*: the ride is slid so its first point lands
 * on that moment of the timeline inside each target, which is what "put this
 * dip where the playhead is" means. Without it the ride keeps the offsets it
 * was copied with, which is what four takes of one phrase want.
 */
export const pasteClipGainEnvelope = defineCommand<{
  clipIds: string[];
  points: { offsetSec: number; gain: number; curve: AutomationCurve }[];
  atTick: number | null;
}>({
  type: 'clipGain.paste',
  label: (p) => (p.clipIds.length === 1 ? 'Paste the clip gain ride' : `Paste the ride onto ${p.clipIds.length} clips`),
  parse: (raw) => {
    const clipIds = idsParam(raw.clipIds);
    if (!clipIds || clipIds.length === 0 || !Array.isArray(raw.points)) return null;

    const points: { offsetSec: number; gain: number; curve: AutomationCurve }[] = [];
    for (const entry of raw.points.slice(0, MAX_POINTS)) {
      if (typeof entry !== 'object' || entry === null) return null;
      const record = entry as Record<string, unknown>;
      const offsetSec = numberParam(record.offsetSec, -24 * 3600, 24 * 3600);
      const gain = numberParam(record.gain, MIN_CLIP_GAIN, MAX_CLIP_GAIN, 1);
      const curve = enumParam(record.curve, CURVES, 'linear');
      if (offsetSec === null || gain === null || !curve) return null;
      points.push({ offsetSec, gain, curve });
    }
    if (points.length === 0) return null;
    const atTick = raw.atTick === undefined || raw.atTick === null ? null : intParam(raw.atTick, 0, Number.MAX_SAFE_INTEGER);
    if (raw.atTick !== undefined && raw.atTick !== null && atTick === null) return null;
    return { clipIds, points, atTick };
  },
  apply: (project, p) => {
    let next = project;
    const first = Math.min(...p.points.map((point) => point.offsetSec));
    for (const clipId of p.clipIds) {
      const clip = audioClip(next, clipId);
      if (!clip) continue;
      // Aimed: the earliest point goes where the tick falls inside this clip,
      // and the rest keep their distances from it.
      const shift = p.atTick === null ? 0 : clipOffsetAtTick(next, clip, p.atTick) - first;
      const pasted = sortedGainPoints(
        p.points.map((point) =>
          makeGainPoint(newGainPointId(), clipSourceAtOffset(next, clip, point.offsetSec + shift), point.gain, point.curve),
        ),
      );
      next = withEnvelope(next, clipId, pasted);
    }
    return next;
  },
});
